import React, { useEffect } from 'react';
import { QUICK_EXIT_URL } from '../config/site';
import Icon from './Icon';

/** Hides the page first (so nobody nearby sees it), then replaces this history entry. */
export function quickExit(): void {
  document.documentElement.style.visibility = 'hidden';
  document.title = 'Google';
  window.location.replace(QUICK_EXIT_URL);
}

type Props = {
  className?: string;
  children?: React.ReactNode;
  /** Use the short "Salir" label on narrow screens. */
  short?: boolean;
  icon?: boolean;
};

/**
 * A real link to Google: it works even if JavaScript fails. With JavaScript it also hides the page
 * before leaving. The Esc key does the same (see useEscapeQuickExit).
 */
export const QuickExitLink: React.FC<Props> = ({ className = 'quick-exit', children, short, icon = true }) => (
  <a
    className={className}
    href={QUICK_EXIT_URL}
    rel="noreferrer"
    title="También puedes presionar Esc"
    aria-label="Salir rápido: cambia esta página por otra"
    onClick={(event) => {
      event.preventDefault();
      quickExit();
    }}
  >
    {icon && <Icon name="exit" />}
    {children ?? (
      <span>
        Salir
        <span className={short ? 'hide-sm' : undefined}> rápido</span>
      </span>
    )}
  </a>
);

/** Esc also exits: only when no menu, dialog or field is active (Esc closes those first). */
export function useEscapeQuickExit(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.body.classList.contains('menu-open')) return;
      if (document.querySelector('dialog[open]')) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      quickExit();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
